/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  useEffect,
  useState,
  useRef,
  useImperativeHandle,
  forwardRef
} from 'react';
import { Dropdown, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import SearchBox from 'components/common/SearchBox';
import Scrollbar from 'components/base/Scrollbar';
import productPosService from 'service/posProduct';
import { ElipceIcon } from 'assets/img/icons/icons';
import defCashImg from 'assets/img/icons/def-cash-img.png';
import './cashSale.scss';

// Utility function to highlight matched letters
const highlightSearchTerm = (name: string, searchTerm: string) => {
  if (!searchTerm || !name) return name;

  const regex = new RegExp(`(${searchTerm})`, 'gi');
  const parts = name.split(regex);

  return parts.map((part, index) =>
    regex.test(part) ? (
      <span
        key={index}
        className="highlight-match"
        style={{
          border: '1px solid #007bff',
          borderRadius: '2px',
          padding: '0 2px',
          backgroundColor: '#e7f1ff'
        }}
      >
        {part}
      </span>
    ) : (
      part
    )
  );
};

const useDebounce = (func: (...args: any[]) => void, delay: number) => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const debouncedFunction = (...args: any[]) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current); // Cancel previous timeout
    }
    timeoutRef.current = setTimeout(() => {
      func(...args);
      timeoutRef.current = null; // Clear ref after execution
    }, delay);
  };

  // Return the debounced function and a cancel method
  debouncedFunction.cancel = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  return debouncedFunction;
};

interface CashSearchBoxProps {
  cashBackListData: any[];
  setCashBackListData: any;
}

export interface CashSearchBoxRef {
  focusAndOpen: () => void;
}

const CashSearchBox = forwardRef<CashSearchBoxRef, CashSearchBoxProps>(
  ({ setCashBackListData, cashBackListData }, ref) => {
    const { t } = useTranslation();
    const searchInputRef = useRef<HTMLInputElement>(null);

    const [openDropdown, setOpenDropdown] = useState(false);
    const [searchInputValue, setSearchInputValue] = useState('');
    const [debouncedSearchValue, setDebouncedSearchValue] =
      useState(searchInputValue);
    const [isProcessing, setIsProcessing] = useState(false);

    useEffect(() => {
      const handler = setTimeout(() => {
        setDebouncedSearchValue(searchInputValue);
      }, 400);

      return () => clearTimeout(handler);
    }, [searchInputValue]);

    const isNumericOnly = (value: string) => {
      return /^\d+$/.test(value.trim());
    };

    const { data: products, refetch } = useQuery(
      [
        'GET_PRODUCT_POS',
        debouncedSearchValue,
        isNumericOnly(debouncedSearchValue) ? 'barcode' : 'search'
      ],
      () => {
        if (
          debouncedSearchValue.length > 2 ||
          isNumericOnly(debouncedSearchValue)
        ) {
          const params = isNumericOnly(debouncedSearchValue)
            ? { barcode: debouncedSearchValue, offset: 0, limit: 10 }
            : { search: debouncedSearchValue, offset: 0, limit: 100 };

          const res = productPosService.getList(params).then((res: any) => {
            return res?.products;
          });

          return res;
        }
      },
      {
        enabled:
          debouncedSearchValue.length > 2 || isNumericOnly(debouncedSearchValue)
      }
    );

    const debouncedHandleGetProduct = useDebounce(async (e: any) => {
      try {
        if (e?.key === 'Enter') {
          const refetchedData = await refetch();

          if (refetchedData?.data?.length > 0) {
            const newProduct = refetchedData.data[0];

            setCashBackListData((prev: any) => {
              const exists = prev.some(
                (item: any) => item.id === newProduct.id
              );
              if (exists) {
                return prev;
              }
              return [...prev, newProduct];
            });

            setOpenDropdown(false);
            setSearchInputValue('');
            searchInputRef.current?.focus();
          }
        }
      } finally {
        setIsProcessing(false);
      }
    }, 500);

    // const handleProductClick = (item: any) => {
    //   setCashBackListData((prev: any) => [...prev, item]);
    //   setOpenDropdown(false);
    //   setSearchInputValue('');
    //   searchInputRef.current?.focus();
    // };

    // Toggle product in cashBackListData
    const toggleProductSelection = (item: any) => {
      setCashBackListData((prev: any[]) => {
        const exists = prev.some(p => p.id === item.id);
        if (exists) {
          return prev.filter(p => p.id !== item.id); // Remove
        } else {
          return [...prev, item]; // Add
        }
      });
    };

    // Check if product is already selected
    const isProductSelected = (itemId: string) => {
      return cashBackListData.some(p => p.id === itemId);
    };

    useImperativeHandle(ref, () => ({
      focusAndOpen: () => {
        searchInputRef.current?.focus();
        setOpenDropdown(true);
      }
    }));

    return (
      <div>
        <Dropdown
          onToggle={() => setOpenDropdown(!openDropdown)}
          show={openDropdown}
        >
          <Dropdown.Toggle
            as="div"
            aria-expanded={openDropdown}
            bsPrefix="toggle"
          >
            <SearchBox
              type="text"
              className="search-input"
              placeholder={t('search_product')}
              value={searchInputValue}
              onChange={({ target }) => {
                setSearchInputValue(target.value);
                setOpenDropdown(true);
              }}
              onKeyDown={e => {
                if (
                  e.key === 'Enter' &&
                  isNumericOnly(searchInputValue) &&
                  !isProcessing
                ) {
                  setOpenDropdown(false);
                  e.preventDefault();
                  setSearchInputValue(searchInputValue);
                  debouncedHandleGetProduct(e);
                }
              }}
              ref={searchInputRef}
            />
          </Dropdown.Toggle>
          <Dropdown.Menu
            className="dropdown-menu border border-300 font-base start-0 py-0 overflow-hidden"
            style={{ width: '100%' }}
          >
            <Scrollbar autoHeight autoHeightMax={'30rem'}>
              <h6 className="text-1000 fs-10 py-2 mb-0 px-3">
                {products?.length || 0}{' '}
                <span className="text-500">{t('results')}</span>{' '}
              </h6>

              {products?.map((item: any) => {
                const checked = isProductSelected(item.id);

                return (
                  <div
                    key={item.id}
                    className="cash-container py-2 mb-0 px-3"
                    // onClick={() => handleProductClick(item)}
                    onClick={e => {
                      // Allow checkbox click without triggering container click
                      if (
                        (e.target as HTMLElement).closest(
                          'input[type="checkbox"]'
                        )
                      ) {
                        e.stopPropagation();
                      } else {
                        toggleProductSelection(item);
                        setSearchInputValue('');
                        setOpenDropdown(false);
                        searchInputRef.current?.focus();
                      }
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="py-2 d-flex gap-2 align-items-center">
                      <div className="table-body-items-switch">
                        <Form.Check
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleProductSelection(item)}
                          onClick={e => e.stopPropagation()}
                        />
                      </div>

                      <div className="file-thumbnail">
                        <img
                          className="fit-cover"
                          src={item?.photo ? item?.photo : defCashImg}
                        />
                      </div>
                      <div className="flex-1">
                        <h6 className="product-name">
                          {highlightSearchTerm(
                            item?.name || '',
                            debouncedSearchValue
                          )}
                        </h6>
                        <p className="mb-0 d-flex gap-2 align-items-center">
                          <span className="cash-cost">
                            {item?.price
                              ? new Intl.NumberFormat('en-US', {
                                  minimumFractionDigits: 0,
                                  maximumFractionDigits: 4,
                                  useGrouping: true
                                })
                                  .format(item?.price)
                                  .replace(/,/g, ' ')
                              : 0}
                          </span>
                          <ElipceIcon />
                          <span className="cash-remainder">
                            {t('remaining_cash')} {item?.total_quantity || 0}
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </Scrollbar>
          </Dropdown.Menu>
        </Dropdown>
      </div>
    );
  }
);

export default CashSearchBox;
